import React, { useState, useMemo } from "react";
import "../ReviewResponsesModal.css";
import { useParams } from "react-router-dom";
import {
  createFollowupQuestion,
  createmanualValidation,
  getQuestionResponse,
} from "../../../../api/common";
import Alert from "../../../../Components/AlertPopup";
import type {
  APIData,
  Response,
  ResponseStatus,
} from "./ReviewResponseModel.types";
import { transformAPIData } from "./transformAPIData";
import QuestionList from "./QuestionList";
import ResponsePanel from "./ResponsePanel";
import AIEvaluationPopup from "./AIEvaluationPopup";
import ClauseInfoPopup from "./ClauseInfoPopup";

interface ReviewResponsesModalProps {
  onClose: () => void;
  apiData?: APIData;
  auditDetails: any;
}

export const ReviewResponsesModal: React.FC<ReviewResponsesModalProps> = ({
  onClose,
  apiData,
  auditDetails,
}) => {
  const { id } = useParams();
  // Transform API data (use provided apiData or fallback to empty)
  const mockData = useMemo(() => {
    if (!apiData || !apiData.questions || apiData.questions.length === 0) {
      return {
        questions: [],
        responses: [],
        clauseInfo: {
          clauseId: "",
          description: "",
          purpose: "",
          artifacts: [],
        },
      };
    }

    return transformAPIData(apiData);
  }, [apiData]);

  const [selectedQuestionId, setSelectedQuestionId] = useState<string>(
    mockData.questions[0]?.id || "",
  );
  const [responseStatusMap, setResponseStatusMap] = useState<
    Record<string, ResponseStatus>
  >({});
  const [showAIEvalPopup, setShowAIEvalPopup] = useState<{
    show: boolean;
    response: Response | null;
  }>({ show: false, response: null });
  const [showClauseInfoPopup, setShowClauseInfoPopup] =
    useState<boolean>(false);
  const [isRegenerating, setIsRegenerating] = useState<Record<string, boolean>>(
    {},
  );
  const [sendQuestionLoading, setSendQuestionLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");

  const selectedQuestion = mockData.questions.find(
    (q) => q.id === selectedQuestionId,
  );
  const responses = mockData.responses.filter(
    (r) => r.questionId === selectedQuestionId,
  );

  // Calculate if all responses are reviewed
  const allReviewsComplete = useMemo(() => {
    if (mockData.responses.length === 0) return false;

    return mockData.responses.every((response) => {
      const status = responseStatusMap[response.id];
      if (!status || !status.userSelection) return false;
      if (status.userSelection === "Needs Follow-up") {
        // Check if reason is provided
        if (
          !status.followUpReason ||
          status.followUpReason.trim().length === 0
        ) {
          return false;
        }
        // Check if at least one question (AI or custom) is actively selected
        const hasSelectedQuestion =
          status.selectedAIQuestion !== null &&
          status.selectedAIQuestion !== undefined &&
          status.selectedAIQuestion !== "";
        return hasSelectedQuestion;
      }
      return true;
    });
  }, [responseStatusMap, mockData.responses]);

  const updateResponseStatus = (
    responseId: string,
    updates: Partial<ResponseStatus>,
  ) => {
    setResponseStatusMap((prev) => {
      const previous = prev[responseId];

      const baseState: ResponseStatus = previous ?? {
        userSelection: null,
        followUpReason: "",
        selectedAIQuestions: [],
        customQuestions: [],
        aiGeneratedQuestions: [],
        selectedAIQuestion: null,
      };

      return {
        ...prev,
        [responseId]: {
          ...baseState,
          ...updates,
        },
      };
    });
  };

  const handleRegenerateQuestions = async (responseId: string) => {
    const status = responseStatusMap[responseId];
    if (!status?.questionCode) return;

    setIsRegenerating((prev) => ({ ...prev, [responseId]: true }));

    try {
      const payload = {
        auditId: id,
        questionCode: status.questionCode,
      };

      const apiResponse = await createFollowupQuestion(payload);

      updateResponseStatus(responseId, {
        aiGeneratedQuestions: apiResponse.generatedFollowupQuestions || [],
        selectedAIQuestion: apiResponse.generatedFollowupQuestions?.[0] || null,
        followUpReason: apiResponse.reason || "",
      });
    } catch (error) {
      console.error("Regenerate failed", error);
    } finally {
      setIsRegenerating((prev) => ({ ...prev, [responseId]: false }));
    }
  };

  const handleSubmit = async () => {
    try {
      setSendQuestionLoading(true);

      const validations = mockData.responses
        .map((response) => {
          const status = responseStatusMap[response.id];
          if (!status || !status.userSelection) return null;

          const isSatisfied = status.userSelection === "Satisfied";

          return {
            questionCode: response.questionCode,
            isSatisfied,
            reason: status.followUpReason || "",
            ...(!isSatisfied && {
              followupQuestion:
                status.selectedAIQuestion || status.customQuestions?.[0] || "",
            }),
          };
        })
        .filter(Boolean);

      const payload = {
        auditId: id,
        validations,
      };

      await createmanualValidation(payload);

      setAlertType("success");
      setAlertMessage("Questions sent successfully!!");
      const formattedOrgs = auditDetails.organizations.map((org: any) => ({
        label: org.organizationName,
        value: org.organizationId,
      }));
      setTimeout(() => {
        getQuestionResponse(parseInt(id!), formattedOrgs[0].value);
      }, 5000);
    } catch (error) {
      setAlertType("error");
      setAlertMessage("Failed to submit review. Please try again.");
    } finally {
      setSendQuestionLoading(false);
    }
  };

  // Handle empty data
  if (!mockData.questions || mockData.questions.length === 0) {
    return (
      <>
        <div
          className="review-modal-overlay"
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              onClose();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div
            className="review-modal-container"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div className="review-modal-header">
              <h2>Review Responses</h2>
              <button className="review-close-button" onClick={onClose}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="review-modal-content">
              <div className="review-no-question">
                No questions available for review
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="review-modal-overlay"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") onClose();
        }}
        role="button"
        tabIndex={0}
      >
        <div
          className="review-modal-container"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="dialog"
        >
          <div className="review-modal-header">
            <h2>Review Responses</h2>
            <button className="review-close-button" onClick={onClose}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="review-modal-content">
            <QuestionList
              questions={mockData.questions}
              selectedQuestionId={selectedQuestionId}
              onSelectQuestion={setSelectedQuestionId}
              responseStatusMap={responseStatusMap}
              responses={mockData.responses}
            />

            <ResponsePanel
              question={selectedQuestion}
              responses={responses}
              responseStatusMap={responseStatusMap}
              onUpdateResponseStatus={updateResponseStatus}
              onShowAIEvaluation={(response) =>
                setShowAIEvalPopup({ show: true, response })
              }
              onShowClauseInfo={() => setShowClauseInfoPopup(true)}
              onRegenerateQuestions={handleRegenerateQuestions}
              isRegenerating={isRegenerating}
            />
          </div>

          <div className="review-modal-footer">
            <div className="review-pending-count">
              {mockData.responses.length -
                Object.keys(responseStatusMap).filter(
                  (key) => responseStatusMap[key]?.userSelection,
                ).length}{" "}
              Pending Review
            </div>
            <button
              className="review-submit-button"
              disabled={!allReviewsComplete || sendQuestionLoading}
              onClick={handleSubmit}
            >
              Submit Review
            </button>
          </div>
        </div>
      </div>

      {showAIEvalPopup.show && showAIEvalPopup.response && (
        <AIEvaluationPopup
          evaluation={showAIEvalPopup.response.aiEvaluation}
          onClose={() => setShowAIEvalPopup({ show: false, response: null })}
        />
      )}

      {showClauseInfoPopup && selectedQuestion && (
        <ClauseInfoPopup
          clauseInfo={mockData.clauseInfo}
          onClose={() => setShowClauseInfoPopup(false)}
        />
      )}
      <Alert
        show={!!alertMessage}
        type={alertType}
        message={alertMessage}
        onClose={() => {
          setAlertMessage("");
          if (alertType === "success") {
            onClose();
            window.location.reload();
          }
        }}
      />
    </>
  );
};
